import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Stack, CircularProgress, Alert, Switch, FormControlLabel, Chip, LinearProgress,
  Select, MenuItem, FormControl, InputLabel, IconButton, Tooltip, Button, TextField, InputAdornment,
  Accordion, AccordionSummary, AccordionDetails,
  Table, TableBody, TableCell, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import PaletteRounded from '@mui/icons-material/PaletteRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import ImageNotSupportedRounded from '@mui/icons-material/ImageNotSupportedRounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import ContentPasteRounded from '@mui/icons-material/ContentPasteRounded';
import FolderOpenRounded from '@mui/icons-material/FolderOpenRounded';

import {
  getInkAssignmentClients, getInkAssignment, upsertInkSeparation, getArtworkThumbnailByRef,
  warmArtworkSeparations, listInkProducts, listCoatings, upsertInkSkuCoating,
  listInkSeries, upsertInkProduct,
  InkAssignmentData, InkAssignmentSku, InkSeparation, InkSeparationUpsert, InkProduct,
} from './productionApi';
import { productionKeys } from './queryKeys';
import { sepLane, thumbLane } from './board/useArtworkSeparations';
import { TOKENS } from './board/ganttConfig';
import ArtworkPathDialog from './ArtworkPathDialog';

const UNITS = [1, 2, 3, 4, 5, 6, 7];

/**
 * Column order across the grid, as the OPERATOR sees the machine.
 *
 * The press runs sheet-first through Unit 1 and out past the coaters, so from
 * where the printer stands the line reads Coating 2, Coating 1, Unit 7 … Unit 1.
 * The grid mirrors that (Jack, 09/09) so a colour's column is where the pot
 * physically is. UNITS itself stays 1..7 — it is the stored print_unit, and the
 * sticker still prints U1 first.
 */
const UNITS_DISPLAY = [...UNITS].reverse();
const THUMB_W = 110;
const MIME = 'application/x-ink-sep';
const LMQ_RED = '#d32f2f';
// Mirrors backend helpers.ink_demand.DEFAULT_INK_MILEAGE_G_PER_M2 — the film
// weight used when a separation has no per-separation override. Shown as the
// placeholder so an empty box reads "using the default", not "unset/zero".
/**
 * The mileage placeholder shown when a separation has NO saved override — i.e.
 * what the demand calc will actually use for it.
 *
 * It is not one number: helpers/ink_demand.py classifies by colour name, giving
 * the seven ECG/process names 1.2 g/m² and everything else (Pantone and brand
 * spots) 1.5. Showing a flat 1.5 here told the operator the wrong default for
 * every CMYK separation. Keep this set in step with `_ECG_NAMES` there.
 */
const ECG_NAMES = new Set(['black', 'cyan', 'magenta', 'yellow', 'orange', 'green', 'violet']);
const ECG_MILEAGE = 1.2;
const PANTONE_MILEAGE = 1.5;
const defaultMileageFor = (name: string) =>
  (ECG_NAMES.has(name.trim().toLowerCase()) ? ECG_MILEAGE : PANTONE_MILEAGE);

// Cloudflow ProofScope deep-link for the Hi-Res 1UP artwork.
//
// The web viewer addresses a file by its cloudflow:// store path, each path
// segment percent-encoded (so spaces become %20), then the whole path
// percent-encoded AGAIN as the ?url= query value — e.g.
//   https://wave.abaris.com.au/portal.cgi?proofscope&url=cloudflow%3A%2F%2F...&topbar=true
// The customer/fingood folders are derived from (customer_code, customer_ref)
// exactly as the backend's _artwork_pdf_path does; only the sub-path differs
// (Hi Res vs the Lo Res the thumbnail pulls). CLOUDFLOW_HOST is the live prod
// tenant — the same host hardcoded backend-side; change both if it ever moves.
const CLOUDFLOW_HOST = 'wave.abaris.com.au';
const ARTWORK_HIRES_SUBPATH = ['Finals', 'PDF Output', 'Hi Res PDF'];

function proofScopeUrl(client: string, sku: string): string | null {
  const cc = (client || '').trim();
  const cr = (sku || '').trim();
  if (!cc || !cr) return null;
  const fingood = cr.toUpperCase().startsWith(cc.toUpperCase()) ? cr : `${cc}${cr}`;
  const segments = ['Production', cc, fingood, ...ARTWORK_HIRES_SUBPATH, `${fingood}.pdf`];
  const path = 'cloudflow://' + segments.map(encodeURIComponent).join('/');
  return `https://${CLOUDFLOW_HOST}/portal.cgi?proofscope&url=${encodeURIComponent(path)}&topbar=true`;
}

/** Best-effort colour match so a Cyan sep surfaces the Cyan inks first. */
function matchesColour(p: InkProduct, sepName: string): boolean {
  const norm = (x: string) => x.toLowerCase().replace(/[^a-z0-9]/g, '');
  const a = norm(p.colour_name);
  const b = norm(sepName);
  return !!a && !!b && (a === b || a.includes(b) || b.includes(a));
}

/**
 * The colour_code a new ink master takes for a separation name — the same rule
 * the catalogue applies: 'Cyan' -> C, 'PANTONE 185 C' -> P185, 'Bulla Buff' ->
 * BULLABUFF. Black is B, never K (house convention, migration 111).
 */
const PROCESS_CODES: Record<string, string> = {
  cyan: 'C', c: 'C', magenta: 'M', m: 'M', yellow: 'Y', y: 'Y', black: 'B', k: 'B', key: 'B',
};
const PMS_RE = /^\s*(?:pantone|pms)\s*[- ]?\s*(\d{2,5})\s*[a-z]*\s*$/i;

function deriveColourCode(name: string): { code: string; kind: 'process' | 'pantone' | 'special' } {
  const n = (name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  if (n in PROCESS_CODES) return { code: PROCESS_CODES[n], kind: 'process' };
  const m = PMS_RE.exec(name || '');
  if (m) return { code: `P${m[1]}`, kind: 'pantone' };
  return { code: (name || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12), kind: 'special' };
}

/**
 * The ink register, ALWAYS FRESH.
 *
 * The working loop here is: hit a separation whose ink doesn't exist yet, go add
 * it in the catalogue, come back, pick it. With a long staleTime that new ink is
 * invisible on return — React Query happily serves the cached list and the
 * dropdown looks broken.
 *
 * In-app that's covered by the catalogue's invalidate, but the catalogue is just
 * as often open in a SECOND BROWSER TAB, which has its own cache and can't
 * invalidate this one. So refetch on mount and on window focus, and hold no
 * staleness: coming back to the tab is exactly the moment the list must be right.
 * It's one small request against a register of a few dozen rows.
 */
function useInkRegister() {
  return useQuery({
    queryKey: productionKeys.inkProducts(),
    queryFn: () => listInkProducts(),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}

function useCoatingRegister() {
  return useQuery({
    queryKey: productionKeys.inkCoatings(),
    queryFn: () => listCoatings(),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}

/** The assignment "recipe" lifted off one SKU, ready to stamp onto another. */
interface SepClip {
  fromSku: string;
  seps: { name: string; print_unit: number | null; ink_code: string | null; mileage_g_per_m2: number | null }[];
  coatings: { 1: string | null; 2: string | null };
}

/** Separations are matched between SKUs by name, case/punctuation-insensitive —
 *  'PANTONE 146 C' and 'Pantone 146C' are the same ink on the floor. */
const sepKey = (name: string) => name.trim().toLowerCase().replace(/[^a-z0-9]/g, '');

type OnDrop = (sku: string, sep: InkSeparation, unit: number | null) => void;
type OnCode = (sku: string, sep: InkSeparation, code: string) => void;
type OnMileage = (sku: string, sep: InkSeparation, mileage: number | null) => void;
type OnCoating = (sku: string, slot: 1 | 2, code: string | null) => void;
type OnCopy = (sku: InkAssignmentSku) => void;
type OnPaste = (sku: InkAssignmentSku) => void;
type OnArtwork = (sku: InkAssignmentSku) => void;
type OnNewInk = (sku: string, sep: InkSeparation) => void;

/**
 * InkAssignmentView — Category List as a compact drag-and-drop grid
 * ([[ink-series-assignment]]). One row per SKU: image · item · description ·
 * unassigned separations · 7 print-unit columns. Drag a separation onto a Unit
 * column, then pick its ink series from the dropdown beneath the chip. Saved per
 * (SKU, separation). Wide-not-tall so a whole category fits without scrolling down.
 */
export default function InkAssignmentView() {
  const queryClient = useQueryClient();
  const [client, setClient] = useState<string>('');
  const [showInactive, setShowInactive] = useState(false);
  const attempted = useRef<Set<string>>(new Set());
  const [warm, setWarm] = useState<{ total: number; done: number }>({ total: 0, done: 0 });

  const clientsQ = useQuery({
    queryKey: productionKeys.inkAssignmentClients(),
    queryFn: getInkAssignmentClients,
    staleTime: 5 * 60 * 1000,
  });

  // NO auto-select. The client list is alphabetical, so auto-selecting the first
  // (AAR — 300+ SKUs) meant that on every mount the warmer flooded Cloudflow with
  // AAR before the operator could pick the client they actually wanted, starving
  // it behind hundreds of stale calls. Nothing loads or warms until a client is
  // explicitly chosen (2026-07-14 regression fix).

  const dataQ = useQuery({
    queryKey: productionKeys.inkAssignment(client, showInactive),
    queryFn: () => getInkAssignment(client, showInactive),
    enabled: !!client,
    staleTime: 60 * 1000,
  });
  const data = dataQ.data;

  // --- background warm of not-yet-extracted artwork (active SKUs only) --------
  // Each active SKU with no cached separations is extracted from Cloudflow once,
  // through `sepLane` (at most 3 in flight). It used to await one warm before
  // starting the next; on a client with ~90 unwarmed SKUs that was ~4 minutes of
  // queue, and because previews shared the same lane the pictures waited behind
  // it too. Thumbnails now have their own lane, and this one runs 3 wide —
  // ~1.1 req/s against Cloudflow's 2 req/s ceiling, and matched to the backend's
  // 3 workers.
  //
  // Still cancellable: every queued call re-checks `cancelled`, so switching
  // client drops the rest immediately rather than letting the previous client
  // starve the one now on screen. The server negative-caches misses.
  useEffect(() => { attempted.current = new Set(); setWarm({ total: 0, done: 0 }); }, [client]);

  useEffect(() => {
    if (!data) return;
    const fresh: string[] = [];
    for (const f of data.formes) {
      for (const s of f.skus) {
        if (s.active && s.separations.length === 0 && !attempted.current.has(s.sku)) fresh.push(s.sku);
      }
    }
    if (fresh.length === 0) return;
    fresh.forEach((sku) => attempted.current.add(sku));
    setWarm((w) => ({ total: w.total + fresh.length, done: w.done }));

    let cancelled = false;
    const warmClient = client;
    (async () => {
      let extracted = 0;
      await Promise.all(fresh.map((sku) => sepLane(async () => {
        if (cancelled) return;                       // client switched away — drop the rest
        try {
          if ((await warmArtworkSeparations(warmClient, sku)) === 'extracted') extracted += 1;
        } catch { /* individual miss/error — negative-cached server-side */ }
        if (cancelled) return;
        setWarm((w) => ({ ...w, done: w.done + 1 }));
      })));
      // Only refresh the grid if we actually pulled new artwork, and only for the
      // client still on screen.
      if (!cancelled && extracted > 0) {
        queryClient.invalidateQueries({ queryKey: productionKeys.inkAssignment(warmClient, showInactive) });
      }
    })();
    return () => { cancelled = true; };
  }, [data, client, showInactive, queryClient]);

  // The grid's cache key includes showInactive, so every read/write of it must
  // too — invalidating (or optimistically patching) inkAssignment(client) alone
  // misses the cache entirely whenever "Show inactive" is on, and edits appear
  // to do nothing until a manual refresh.
  const gridKey = productionKeys.inkAssignment(client, showInactive);

  // A colour/coating reassignment changes the ink sticker and the demand roll-up
  // for every open job on this SKU — so invalidate those caches too, not just the
  // assignment grid. Without this, an already-open Ink Sticker (staleTime 60s) or
  // the Balance demand keeps showing the OLD ink until a hard refresh. We don't
  // track which jobs use the SKU, so invalidate the whole sticker/demand families.
  const invalidateAfterAssign = () => {
    queryClient.invalidateQueries({ queryKey: gridKey });
    queryClient.invalidateQueries({ queryKey: ['production', 'ink-sticker'] });
    queryClient.invalidateQueries({ queryKey: productionKeys.inkDemandSummary() });
  };

  const mutation = useMutation({
    mutationFn: (p: InkSeparationUpsert) => upsertInkSeparation(p),
    onSettled: invalidateAfterAssign,
  });

  const coatingMutation = useMutation({
    mutationFn: (p: { customer_code: string; sku: string; slot: 1 | 2; ink_code: string | null }) =>
      upsertInkSkuCoating(p),
    onSettled: invalidateAfterAssign,
  });

  // One optimistic patch path for both moves (unit) and series edits.
  const patch = (sku: string, sep: InkSeparation, changes: Partial<InkSeparation>) => {
    const next = { ...sep, ...changes };
    queryClient.setQueryData<InkAssignmentData>(gridKey, (old) =>
      !old ? old : {
        ...old,
        formes: old.formes.map((f) => ({
          ...f,
          skus: f.skus.map((s) => s.sku !== sku ? s : {
            ...s,
            separations: s.separations.map((x) => x.name !== sep.name ? x : next),
          }),
        })),
      });
    const payload: InkSeparationUpsert = {
      customer_code: client, sku, colour_name: sep.name,
      kind: sep.kind, hex_colour: sep.hex_colour,
      print_unit: next.print_unit, ink_code: next.ink_code,
    };
    // Only carry mileage when this edit actually touched it — the backend leaves
    // a saved override alone when the key is absent, so a unit/ink move never
    // clobbers it, and a paste can carry it through explicitly.
    if ('mileage_g_per_m2' in changes) payload.mileage_g_per_m2 = next.mileage_g_per_m2;
    mutation.mutate(payload);
  };
  const onDrop: OnDrop = (sku, sep, unit) => { if (sep.print_unit !== unit) patch(sku, sep, { print_unit: unit }); };
  const onCode: OnCode = (sku, sep, code) => patch(sku, sep, { ink_code: code.trim() || null });
  // The register has nothing for this colour: create the ink master here, from
  // the separation, and assign it in the same motion. The catalogue stays the
  // master — it's just reachable from where the need appears.
  const [newInk, setNewInk] = useState<{ sku: string; sep: InkSeparation } | null>(null);
  const onNewInk: OnNewInk = (sku, sep) => setNewInk({ sku, sep });
  const onMileage: OnMileage = (sku, sep, mileage) => {
    if ((sep.mileage_g_per_m2 ?? null) !== mileage) patch(sku, sep, { mileage_g_per_m2: mileage });
  };

  // Coating slots live per-SKU (not per-separation) — one optimistic patch path.
  const onCoating: OnCoating = (sku, slot, code) => {
    queryClient.setQueryData<InkAssignmentData>(gridKey, (old) =>
      !old ? old : {
        ...old,
        formes: old.formes.map((f) => ({
          ...f,
          skus: f.skus.map((s) => s.sku !== sku ? s : {
            ...s,
            coatings: { ...s.coatings, [String(slot)]: code },
          }),
        })),
      });
    coatingMutation.mutate({ customer_code: client, sku, slot, ink_code: code });
  };

  // --- copy / paste an assignment across SKUs ---------------------------------
  // Most SKUs on a forme share the same separations, so re-picking every unit and
  // every ink by hand is the bulk of the work here. Copy lifts the whole recipe
  // off one item; paste stamps it onto another, matching separations BY NAME.
  //
  // Paste only touches separations the target actually has and the source had
  // assigned. Anything the target has that the source didn't is left alone — those
  // are the exceptions, and they are the entire point: the operator should be left
  // looking at exactly the ones that still need a decision, and nothing else.
  const [clip, setClip] = useState<SepClip | null>(null);
  const [pasteMsg, setPasteMsg] = useState<{ severity: 'success' | 'warning'; text: string } | null>(null);

  const onCopy: OnCopy = (sku) => {
    setClip({
      fromSku: sku.sku,
      seps: sku.separations.map((s) => ({
        name: s.name, print_unit: s.print_unit, ink_code: s.ink_code,
        mileage_g_per_m2: s.mileage_g_per_m2,
      })),
      coatings: { 1: sku.coatings?.['1'] ?? null, 2: sku.coatings?.['2'] ?? null },
    });
    setPasteMsg(null);
  };

  const onPaste: OnPaste = (target) => {
    if (!clip || clip.fromSku === target.sku) return;

    // Only source seps that were actually assigned are worth stamping — copying
    // "unassigned" over a target is not a decision, it's just noise.
    const src = new Map(
      clip.seps
        .filter((s) => s.print_unit != null || s.ink_code || s.mileage_g_per_m2 != null)
        .map((s) => [sepKey(s.name), s]),
    );

    const applied: string[] = [];
    const exceptions: string[] = [];

    for (const sep of target.separations) {
      const hit = src.get(sepKey(sep.name));
      if (!hit) { exceptions.push(sep.name); continue; }
      if (sep.print_unit === hit.print_unit
        && (sep.ink_code ?? null) === (hit.ink_code ?? null)
        && (sep.mileage_g_per_m2 ?? null) === (hit.mileage_g_per_m2 ?? null)) {
        applied.push(sep.name);   // already identical — count it, don't re-write it
        continue;
      }
      // Mileage rides along with the unit + ink so a pasted recipe is complete.
      patch(target.sku, sep, {
        print_unit: hit.print_unit, ink_code: hit.ink_code,
        mileage_g_per_m2: hit.mileage_g_per_m2,
      });
      applied.push(sep.name);
    }

    for (const key of ['1', '2'] as const) {
      const slot = (key === '1' ? 1 : 2) as 1 | 2;
      const want = clip.coatings[slot];
      const have = target.coatings?.[key] ?? null;
      if (want !== have) onCoating(target.sku, slot, want);
    }

    setPasteMsg(
      exceptions.length
        ? {
          severity: 'warning',
          text: `${target.sku}: matched ${applied.length} separation${applied.length === 1 ? '' : 's'} from ${clip.fromSku}. `
            + `Still needs you: ${exceptions.join(', ')}.`,
        }
        : {
          severity: 'success',
          text: `${target.sku}: all ${applied.length} separation${applied.length === 1 ? '' : 's'} matched ${clip.fromSku}. Nothing left to do.`,
        },
    );

    // ONE COPY = ONE PASTE. The clipboard is spent here, so pasting again — even
    // onto the same item — means copying again first. A clipboard that stays armed
    // across rows is one stray click away from silently overwriting a SKU you'd
    // already finished, and on a 53-SKU forme you would not notice.
    setClip(null);
  };

  // --- re-point one item's artwork --------------------------------------------
  // The separations on a row are only ever as right as the FILE they were read
  // from, and Pre-Press files plenty of artwork outside the naming convention
  // (that's what production.artwork_path_overrides exists for). One dialog for
  // the whole grid — it's modal, so only one row is ever being fixed at a time.
  // Held by SKU code, not by object: the grid refetches after a save, and a
  // captured row object would leave the dialog showing the pre-save path.
  const [pathSkuCode, setPathSkuCode] = useState<string | null>(null);
  const pathSku = useMemo(() => {
    if (!pathSkuCode || !data) return null;
    for (const f of data.formes) {
      const hit = f.skus.find((s) => s.sku === pathSkuCode);
      if (hit) return hit;
    }
    return null;
  }, [pathSkuCode, data]);

  const overriddenCount = useMemo(
    () => (data?.formes ?? []).reduce(
      (n, f) => n + f.skus.filter((s) => s.artwork_overridden).length, 0),
    [data],
  );

  // A re-pull changes the separations, so it changes everything downstream of
  // them: this grid, the row's thumbnail, any open Ink Sticker, the demand roll-up.
  const onArtworkSaved = () => {
    queryClient.invalidateQueries({ queryKey: gridKey });
    queryClient.invalidateQueries({ queryKey: ['production', 'artwork-thumb-ref', client, pathSkuCode] });
    queryClient.invalidateQueries({ queryKey: ['production', 'ink-sticker'] });
    queryClient.invalidateQueries({ queryKey: productionKeys.inkDemandSummary() });
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 0.5 }}>
        <PaletteRounded sx={{ color: TOKENS.accent }} />
        <Typography sx={{ fontWeight: 700, fontSize: 20 }}>Ink assignment — category list</Typography>
      </Stack>
      <Typography sx={{ fontSize: 13, color: TOKENS.ink3, mb: 1.5 }}>
        Pick a client to load its category list. Separations read live from the Cloudflow artwork —
        drag one onto a print unit, then pick its ink series.
      </Typography>

      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }}>
        <FormControl size="small" sx={{ minWidth: 320 }}>
          <InputLabel>Client</InputLabel>
          <Select label="Client" value={client} displayEmpty
            onChange={(e) => setClient(String(e.target.value))}>
            <MenuItem value="" disabled>
              <em>Select a client…</em>
            </MenuItem>
            {(clientsQ.data ?? []).map((c) => (
              <MenuItem key={c.client} value={c.client}>
                {c.client} — {c.skus} SKUs · {c.formes} forme{c.formes === 1 ? '' : 's'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {data && (
          <Typography sx={{ fontSize: 12.5, color: TOKENS.ink3 }}>
            {data.summary.assigned}/{data.summary.separations} allocated · {data.summary.skus} SKUs · {data.summary.formes} formes
          </Typography>
        )}
        {/* Client-level count: an override is invisible until you happen to scroll
            past the row that has one, and on a 133-SKU client you won't. */}
        {overriddenCount > 0 && (
          <Chip size="small" label={`✎ ${overriddenCount} on a corrected artwork path`}
            sx={{
              height: 20, fontSize: 11, fontWeight: 700,
              color: TOKENS.accent, bgcolor: `${TOKENS.accent}1f`,
              border: `1px solid ${TOKENS.accent}55`,
            }} />
        )}
        <FormControlLabel sx={{ ml: 'auto' }}
          control={<Switch size="small" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
          label={<Typography sx={{ fontSize: 12.5, color: TOKENS.ink2 }}>Show inactive items</Typography>} />
      </Stack>

      {warm.done < warm.total && (
        <Box sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: 12, color: TOKENS.ink3, mb: 0.5 }}>
            Reading separations for {warm.total - warm.done} more SKU
            {warm.total - warm.done === 1 ? '' : 's'} from Cloudflow (one at a time)…
          </Typography>
          <LinearProgress variant="determinate"
            value={warm.total ? (warm.done / warm.total) * 100 : 0} sx={{ borderRadius: 1 }} />
        </Box>
      )}

      {clip && (
        <Alert
          severity="info" icon={<ContentCopyRounded fontSize="small" />}
          sx={{ mb: 1.5, py: 0.25, fontSize: 12.5 }}
          action={<Button size="small" onClick={() => { setClip(null); setPasteMsg(null); }}>Clear</Button>}
        >
          Copied <strong>{clip.fromSku}</strong> ({clip.seps.filter((s) => s.print_unit != null || s.ink_code).length} assigned
          separation{clip.seps.filter((s) => s.print_unit != null || s.ink_code).length === 1 ? '' : 's'})
          — hit <strong>Paste</strong> on another item to stamp the matching separations into the same units.
          Good for <strong>one</strong> paste; copy again for the next.
        </Alert>
      )}

      {pasteMsg && (
        <Alert severity={pasteMsg.severity} sx={{ mb: 1.5, py: 0.25, fontSize: 12.5 }}
          onClose={() => setPasteMsg(null)}>
          {pasteMsg.text}
        </Alert>
      )}

      {!client && !clientsQ.isLoading && (
        <Stack alignItems="center" sx={{ py: 8 }} spacing={1}>
          <PaletteRounded sx={{ fontSize: 40, color: TOKENS.ink3, opacity: 0.5 }} />
          <Typography sx={{ fontSize: 14, color: TOKENS.ink2, fontWeight: 600 }}>Select a client to begin</Typography>
          <Typography sx={{ fontSize: 12.5, color: TOKENS.ink3 }}>
            Its category list loads on demand, and artwork is read from Cloudflow one item at a time.
          </Typography>
        </Stack>
      )}
      {(clientsQ.isLoading || (!!client && dataQ.isLoading)) && (
        <Stack alignItems="center" sx={{ py: 6 }} spacing={1.5}>
          <CircularProgress size={28} />
          <Typography sx={{ fontSize: 13, color: TOKENS.ink3 }}>Reading artwork separations…</Typography>
        </Stack>
      )}
      {dataQ.isError && (
        <Alert severity="error">Couldn’t load: {(dataQ.error as Error)?.message ?? 'unknown error'}</Alert>
      )}

      {data && data.formes.map((f, i) => (
        <Accordion key={f.forme} defaultExpanded={i === 0} disableGutters elevation={0}
          TransitionProps={{ unmountOnExit: true }}
          sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '10px', mb: 1, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreRounded />}>
            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
              {f.forme} <span style={{ color: TOKENS.ink3, fontWeight: 400 }}>· {f.skus.length} SKUs</span>
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, px: 1 }}>
            <FormeTable client={client} skus={f.skus} onDrop={onDrop} onCode={onCode} onMileage={onMileage} onCoating={onCoating}
              onCopy={onCopy} onPaste={onPaste} onArtwork={(s) => setPathSkuCode(s.sku)} onNewInk={onNewInk}
              clipFrom={clip?.fromSku ?? null} canPaste={!!clip} />
          </AccordionDetails>
        </Accordion>
      ))}

      {newInk && (
        <NewInkDialog
          client={client}
          sku={newInk.sku}
          sep={newInk.sep}
          onClose={() => setNewInk(null)}
          onCreated={(s, sep, inkKey) => onCode(s, sep, inkKey)}
        />
      )}

      {pathSku && (
        <ArtworkPathDialog
          open
          customerCode={client}
          customerRef={pathSku.sku}
          currentPath={pathSku.artwork_path}
          overridden={pathSku.artwork_overridden}
          onClose={() => setPathSkuCode(null)}
          onSaved={onArtworkSaved}
        />
      )}
    </Box>
  );
}

const HEAD = { fontWeight: 700, fontSize: 11, color: TOKENS.ink3, py: 0.5 } as const;

function FormeTable({ client, skus, onDrop, onCode, onMileage, onCoating, onCopy, onPaste, onArtwork, onNewInk, clipFrom, canPaste }: {
  client: string; skus: InkAssignmentSku[]; onDrop: OnDrop; onCode: OnCode; onMileage: OnMileage; onCoating: OnCoating;
  onCopy: OnCopy; onPaste: OnPaste; onArtwork: OnArtwork; onNewInk: OnNewInk;
  clipFrom: string | null; canPaste: boolean;
}) {
  return (
    <Box>
      {/* tableLayout fixed + width 100%: the meta columns take fixed widths and
          the 7 units + 2 coaters share the rest — so everything fits the viewport
          with no horizontal scroll (they just compress on narrower screens).
          The meta columns are kept deliberately tight, because every pixel taken
          here comes straight out of the 9 ink columns, and those now WRAP the full
          ink description rather than clipping it. */}
      <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', '& td, & th': { borderColor: 'rgba(0,0,0,0.06)', px: 0.5 } }}>
        <TableHead>
          <TableRow>
            {/* Art must be at least as wide as the thumb it holds (see SkuThumb),
                or the image spills into Item and the SKU code lands on top of it. */}
            <TableCell sx={{ ...HEAD, width: 52 }}>Art</TableCell>
            <TableCell sx={{ ...HEAD, width: 88 }}>Item</TableCell>
            <TableCell sx={{ ...HEAD, width: 104 }}>Description</TableCell>
            <TableCell sx={{ ...HEAD, width: 132 }}>Separations · Prism ink</TableCell>
            <TableCell align="center" sx={{ ...HEAD }}>Coat 2</TableCell>
            <TableCell align="center" sx={{ ...HEAD }}>Coat 1</TableCell>
            {UNITS_DISPLAY.map((u) => (
              <TableCell key={u} align="center" sx={{ ...HEAD }}>U{u}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {skus.map((sku) => (
            <SkuRow key={sku.sku} client={client} sku={sku} onDrop={onDrop} onCode={onCode} onMileage={onMileage} onCoating={onCoating}
              onCopy={onCopy} onPaste={onPaste} onArtwork={onArtwork} onNewInk={onNewInk}
              isClipSource={clipFrom === sku.sku} canPaste={canPaste} />
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function SkuRow({ client, sku, onDrop, onCode, onMileage, onCoating, onCopy, onPaste, onArtwork, onNewInk, isClipSource, canPaste }: {
  client: string; sku: InkAssignmentSku; onDrop: OnDrop; onCode: OnCode; onMileage: OnMileage; onCoating: OnCoating;
  onCopy: OnCopy; onPaste: OnPaste; onArtwork: OnArtwork; onNewInk: OnNewInk;
  isClipSource: boolean; canPaste: boolean;
}) {
  const pool = sku.separations.filter((s) => s.print_unit == null);
  const byUnit = (u: number) => sku.separations.filter((s) => s.print_unit === u);
  const hasAssigned = sku.separations.some((s) => s.print_unit != null || s.ink_code);
  return (
    <TableRow hover sx={{
      verticalAlign: 'top',
      opacity: sku.active ? 1 : 0.55,
      bgcolor: isClipSource
        ? 'rgba(203,96,21,0.07)'
        : (sku.active ? undefined : 'rgba(0,0,0,0.025)'),
      outline: isClipSource ? `1px solid ${TOKENS.accent}55` : 'none', outlineOffset: '-1px',
    }}>
      <TableCell sx={{ py: 0.75, overflow: 'hidden' }}><SkuThumb client={client} sku={sku.sku} /></TableCell>
      <TableCell sx={{ py: 0.75 }}>
        <Typography sx={{
          fontFamily: TOKENS.monoFont, fontWeight: 700, fontSize: 11.5,
          lineHeight: 1.2, wordBreak: 'break-all',
        }}>{sku.sku}</Typography>
        {!sku.active && (
          <Chip size="small" label="INACTIVE"
            sx={{ height: 15, fontSize: 8.5, fontWeight: 800, mt: 0.25, bgcolor: 'rgba(0,0,0,0.12)' }} />
        )}
        {/* An override silently redirects EVERY job on this item, so it gets a
            standing badge — not just a tinted icon you'd have to know to look
            for. Same ✎ mark the Ink Sticker prints, so the two screens read
            the same way. The path itself is one hover away. */}
        {sku.artwork_overridden && (
          <Tooltip arrow title={
            <Box sx={{ py: 0.25 }}>
              <Box sx={{ fontWeight: 700, mb: 0.25 }}>Artwork path corrected</Box>
              <Box sx={{ fontFamily: TOKENS.monoFont, fontSize: 10, wordBreak: 'break-all' }}>
                {sku.artwork_path}
              </Box>
            </Box>
          }>
            <Chip size="small" label="✎ ARTWORK"
              sx={{
                height: 15, fontSize: 8.5, fontWeight: 800, mt: 0.25,
                color: TOKENS.accent, bgcolor: `${TOKENS.accent}1f`,
                border: `1px solid ${TOKENS.accent}55`,
              }} />
          </Tooltip>
        )}
        <Stack direction="row" spacing={0.25} sx={{ mt: 0.25 }}>
          <Tooltip title={hasAssigned
            ? 'Copy this item’s units + inks'
            : 'Nothing assigned on this item yet'}>
            {/* span: a disabled button doesn't fire the events Tooltip needs */}
            <span>
              <IconButton size="small" disabled={!hasAssigned} onClick={() => onCopy(sku)}
                sx={{ p: 0.25, color: isClipSource ? TOKENS.accent : undefined }}>
                <ContentCopyRounded sx={{ fontSize: 13 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={!canPaste
            ? 'Copy an item first'
            : (isClipSource ? 'This is the copied item' : 'Paste the matching separations into the same units')}>
            <span>
              <IconButton size="small" disabled={!canPaste || isClipSource} onClick={() => onPaste(sku)}
                sx={{ p: 0.25 }}>
                <ContentPasteRounded sx={{ fontSize: 13 }} />
              </IconButton>
            </span>
          </Tooltip>
          {/* The escape hatch for "these separations are wrong / there are none":
              point the item at the real PDF and read it again. Accented when this
              item is already on a corrected path, so a modified row is visible at
              a glance rather than only inside the dialog. */}
          <Tooltip title={sku.separations.length === 0
            ? 'No separations found — pick the right artwork file'
            : (sku.artwork_overridden
              ? 'Artwork path was corrected — change it or re-read the file'
              : 'Change the artwork file / re-read its separations')}>
            <IconButton size="small" onClick={() => onArtwork(sku)}
              sx={{ p: 0.25, color: sku.artwork_overridden ? TOKENS.accent : undefined }}>
              <FolderOpenRounded sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </TableCell>
      <TableCell sx={{ py: 0.75 }}>
        <Typography sx={{ fontSize: 12, color: TOKENS.ink2, lineHeight: 1.25 }} title={sku.description ?? ''}>
          {sku.description ?? '—'}
        </Typography>
      </TableCell>
      <DropCell sku={sku.sku} unit={null} seps={pool} onDrop={onDrop} onCode={onCode} pool inkSpec={sku.ink_spec} onNewInk={onNewInk} />
      <TableCell sx={{ py: 0.75, verticalAlign: 'top' }}>
        <CoatingSelect sku={sku.sku} slot={2} value={sku.coatings?.['2'] ?? null} onCoating={onCoating} />
      </TableCell>
      <TableCell sx={{ py: 0.75, verticalAlign: 'top' }}>
        <CoatingSelect sku={sku.sku} slot={1} value={sku.coatings?.['1'] ?? null} onCoating={onCoating} />
      </TableCell>
      {UNITS_DISPLAY.map((u) => (
        <DropCell key={u} sku={sku.sku} unit={u} seps={byUnit(u)} onDrop={onDrop} onCode={onCode}
          onMileage={onMileage} onNewInk={onNewInk} />
      ))}
    </TableRow>
  );
}

function DropCell({ sku, unit, seps, onDrop, onCode, onMileage, pool, inkSpec, onNewInk }: {
  sku: string; unit: number | null; seps: InkSeparation[]; onDrop: OnDrop; onCode: OnCode;
  onMileage?: OnMileage; pool?: boolean; inkSpec?: string | null; onNewInk?: OnNewInk;
}) {
  const [over, setOver] = useState(false);
  return (
    <TableCell
      onDragOver={(e) => { e.preventDefault(); if (!over) setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault(); setOver(false);
        try {
          const p = JSON.parse(e.dataTransfer.getData(MIME)) as { sku: string; sep: InkSeparation };
          if (p.sku === sku) onDrop(sku, p.sep, unit);
        } catch { /* ignore malformed */ }
      }}
      sx={{
        py: 0.75, verticalAlign: 'top',
        bgcolor: over ? 'rgba(203,96,21,0.12)' : (pool ? 'transparent' : 'rgba(0,0,0,0.015)'),
        outline: over ? `2px dashed ${TOKENS.accent}` : 'none', outlineOffset: '-2px',
        transition: 'background-color 120ms',
      }}
    >
      {pool ? (
        <>
          <Stack spacing={0.25} sx={{ minHeight: 22 }}>
            {seps.map((s) => (
              <PoolSep key={s.name} sku={sku} sep={s} onNewInk={onNewInk} />
            ))}
          </Stack>
          {inkSpec && (
            <Typography sx={{ mt: 0.5, fontSize: 9, color: TOKENS.ink3, lineHeight: 1.25, wordBreak: 'break-word' }} title={inkSpec}>
              <Box component="span" sx={{ fontWeight: 700 }}>Prism:</Box> {inkSpec}
            </Typography>
          )}
        </>
      ) : (
        <Stack spacing={0.75} sx={{ minHeight: 22 }}>
          {seps.map((s) => (
            <AssignedSep key={s.name} sku={sku} sep={s} onCode={onCode} onMileage={onMileage} onNewInk={onNewInk} />
          ))}
        </Stack>
      )}
    </TableCell>
  );
}

/** One assigned separation: chip + series picker + demand mileage, with the cell
 *  tinted by the chosen ink's series colour — the same shade the Ink Sticker uses. */
function AssignedSep({ sku, sep, onCode, onMileage, onNewInk }: {
  sku: string; sep: InkSeparation; onCode: OnCode; onMileage?: OnMileage; onNewInk?: OnNewInk;
}) {
  const productsQ = useInkRegister();
  const current = (productsQ.data ?? []).find((p) => p.ink_key === (sep.ink_code ?? ''));
  const tint = current?.series_hex ?? null;
  // On a press unit but with no ink chosen: the artwork says this colour runs
  // here, and we know the kg, but nothing can be kitted for it — you can't draw
  // "Cyan" off a shelf. Half-done is the one state that must not look finished
  // (Jack, 09/09), so it gets a red dashed cell rather than a quiet empty picker.
  const incomplete = !sep.ink_code;
  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'stretch',
      borderRadius: '5px', px: 0.5, py: 0.35,
      bgcolor: incomplete ? `${TOKENS.bad}0F` : tint ? `${tint}33` : 'transparent',
      border: incomplete
        ? `1px dashed ${TOKENS.bad}99`
        : tint ? `1px solid ${tint}66` : '1px solid transparent',
    }}>
      <Box sx={{ display: 'flex' }}><ColourChip sku={sku} sep={sep} /></Box>
      {incomplete && (
        <Typography sx={{
          fontSize: 8.5, fontWeight: 800, letterSpacing: 0.3, color: TOKENS.bad,
          textTransform: 'uppercase', lineHeight: 1.3, mt: 0.2,
        }}>
          no ink sku selected
        </Typography>
      )}
      <SeriesSelect sku={sku} sep={sep} onCode={onCode} onNewInk={onNewInk} />
      {onMileage && <MileageField sku={sku} sep={sep} onMileage={onMileage} />}
    </Box>
  );
}

/**
 * The per-separation ink demand knob: a tiny g/m² field that overrides the
 * system default for THIS colour on THIS SKU. Empty = "use the default"
 * (placeholder shows it), a positive number tunes the demand kg everywhere it's
 * computed (Ink Demand, the Sticker, the roll-up). Commits on blur / Enter, so a
 * half-typed value never fires a save; an override shows in the accent colour.
 */
function MileageField({ sku, sep, onMileage }: {
  sku: string; sep: InkSeparation; onMileage: OnMileage;
}) {
  const saved = sep.mileage_g_per_m2;
  const [val, setVal] = useState(saved == null ? '' : String(saved));
  useEffect(() => { setVal(saved == null ? '' : String(saved)); }, [saved]);

  const commit = () => {
    const t = val.trim();
    if (t === '') { onMileage(sku, sep, null); return; }
    const n = Number(t);
    if (!isFinite(n) || n <= 0) { setVal(saved == null ? '' : String(saved)); return; }  // reject, revert
    onMileage(sku, sep, n);
  };

  return (
    <TextField
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      variant="standard"
      placeholder={String(defaultMileageFor(sep.name))}
      inputProps={{ inputMode: 'decimal', 'aria-label': 'ink mileage g/m²',
        style: { fontSize: 10, padding: '1px 0', textAlign: 'right' } }}
      InputProps={{
        disableUnderline: false,
        endAdornment: (
          <InputAdornment position="end" sx={{ ml: 0.25 }}>
            <span style={{ fontSize: 8.5, color: TOKENS.ink3 }}>g/m²</span>
          </InputAdornment>
        ),
      }}
      sx={{
        mt: 0.4, width: '100%',
        '& .MuiInput-input': { color: saved != null ? TOKENS.accent : undefined, fontWeight: saved != null ? 700 : 400 },
      }}
    />
  );
}

/** Sentinel value for the dropdown's "create one" row. Not an ink key — no ink
 *  key contains a space, so it can never collide with a real product. */
const NEW_INK_OPTION = '\u0000 new ink';

function SeriesSelect({ sku, sep, onCode, onNewInk }: {
  sku: string; sep: InkSeparation; onCode: OnCode; onNewInk?: OnNewInk;
}) {
  // Shared across every cell — React Query dedupes to a single request.
  const productsQ = useInkRegister();
  const products = productsQ.data ?? [];
  const current = sep.ink_code ?? '';
  const currentProduct = products.find((p) => p.ink_key === current);
  const isLegacy = !!current && !currentProduct;

  // Inks for this separation's colour first; if none match, offer the whole
  // register. Always keep the current selection present as an option.
  const matched = products.filter((p) => matchesColour(p, sep.name));
  const options = matched.length ? matched : products;
  const list = currentProduct && !options.some((p) => p.ink_key === current)
    ? [currentProduct, ...options] : options;

  return (
    <Select
      variant="standard" fullWidth displayEmpty value={current}
      onChange={(e) => {
        const v = String(e.target.value);
        if (v === NEW_INK_OPTION) { onNewInk?.(sku, sep); return; }
        onCode(sku, sep, v);
      }}
      renderValue={(val) => {
        const v = String(val);
        if (!v) return <span style={{ color: '#999', fontSize: 11 }}>— ink —</span>;
        const p = products.find((x) => x.ink_key === v);
        // Show the meaningful description (e.g. HUBER REMEDY APPLE CRISP ECO CO),
        // not the code (v). It WRAPS rather than clipping to 'HUBER RE…': the whole
        // point of this cell is knowing which ink is loaded, and a truncated name
        // that needs a hover to disambiguate isn't an answer. The row grows a line
        // or two; that's a cheap price. Hover still reveals the ink key.
        return (
          <Box component="span" title={v}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.4, minWidth: 0, maxWidth: '100%' }}>
            {p?.series_hex && (
              <Box component="span" sx={{
                width: 7, height: 7, borderRadius: '50%', bgcolor: p.series_hex,
                flexShrink: 0, mt: '4px',
              }} />
            )}
            <span style={{
              fontSize: 10, fontWeight: 600, color: p?.low_migration ? LMQ_RED : undefined,
              whiteSpace: 'normal', overflowWrap: 'anywhere', lineHeight: 1.15,
            }}>{p?.description ?? v}</span>
          </Box>
        );
      }}
      sx={{
        mt: 0.4, fontSize: 10.5,
        // The Select's own cell clamps to one line by default — unclamp it so the
        // wrapped description above is actually allowed to occupy the lines it needs.
        '& .MuiSelect-select': {
          py: '1px', fontSize: 10.5,
          whiteSpace: 'normal !important', overflow: 'visible', textOverflow: 'clip',
          minHeight: 0,
        },
      }}
    >
      <MenuItem value=""><span style={{ color: '#999', fontSize: 11 }}>— ink —</span></MenuItem>
      {isLegacy && <MenuItem value={current} sx={{ fontSize: 11 }}>{current} (legacy)</MenuItem>}
      {list.map((p) => (
        <MenuItem key={p.ink_key} value={p.ink_key} sx={{ fontSize: 11, gap: 0.75 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '2px', flexShrink: 0, bgcolor: p.hex_colour ?? '#ccc', border: '1px solid rgba(0,0,0,0.2)' }} />
          <span style={{ fontFamily: TOKENS.monoFont, fontWeight: 700, color: p.low_migration ? LMQ_RED : undefined }}>{p.ink_key}</span>
          <span style={{ color: '#888', fontSize: 10 }}>{p.description}</span>
        </MenuItem>
      ))}
      {onNewInk && (
        <MenuItem value={NEW_INK_OPTION} sx={{ fontSize: 11, gap: 0.75, color: TOKENS.accent, fontWeight: 700 }}>
          <AddRounded sx={{ fontSize: 13 }} /> Create a new ink…
        </MenuItem>
      )}
    </Select>
  );
}

/** A coater-slot picker (Coating 1 / Coating 2). One per SKU, not per sep.
 *  Coatings are the 'COATINGS' series in the catalogue; the cell tints with the
 *  uniform varnish colour once one is chosen. */
function CoatingSelect({ sku, slot, value, onCoating }: {
  sku: string; slot: 1 | 2; value: string | null; onCoating: OnCoating;
}) {
  const coatingsQ = useCoatingRegister();
  const coatings = coatingsQ.data ?? [];
  const current = value ?? '';
  const p = coatings.find((c) => c.ink_key === current);
  const tint = p?.series_hex ?? p?.hex_colour ?? null;
  const isLegacy = !!current && !p;

  return (
    <Box sx={{
      borderRadius: '5px', px: 0.5, py: 0.4,
      bgcolor: tint ? `${tint}33` : 'transparent',
      border: tint ? `1px solid ${tint}66` : '1px solid transparent',
    }}>
      <Select
        variant="standard" fullWidth displayEmpty value={current}
        onChange={(e) => onCoating(sku, slot, String(e.target.value) || null)}
        renderValue={(val) => {
          const v = String(val);
          if (!v) return <span style={{ color: '#999', fontSize: 11 }}>— coating —</span>;
          const c = coatings.find((x) => x.ink_key === v);
          // Wraps, like the ink cells — a clipped coating name is no more use than
          // a clipped ink name.
          return (
            <span title={v} style={{
              fontSize: 10, fontWeight: 600, display: 'block', maxWidth: '100%',
              whiteSpace: 'normal', overflowWrap: 'anywhere', lineHeight: 1.15,
            }}>{c?.description ?? v}</span>
          );
        }}
        sx={{
          fontSize: 10.5,
          '& .MuiSelect-select': {
            py: '1px', fontSize: 10.5,
            whiteSpace: 'normal !important', overflow: 'visible', textOverflow: 'clip',
            minHeight: 0,
          },
        }}
      >
        <MenuItem value=""><span style={{ color: '#999', fontSize: 11 }}>— none —</span></MenuItem>
        {isLegacy && <MenuItem value={current} sx={{ fontSize: 11 }}>{current} (legacy)</MenuItem>}
        {coatings.map((c) => (
          <MenuItem key={c.ink_key} value={c.ink_key} sx={{ fontSize: 11, gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', flexShrink: 0, bgcolor: c.hex_colour ?? '#ccc', border: '1px solid rgba(0,0,0,0.2)' }} />
            <span>{c.description}</span>
            <span style={{ color: '#888', fontSize: 10, fontFamily: TOKENS.monoFont }}>{c.colour_code}</span>
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}

/**
 * A separation still in the pool (no print unit). Two states:
 *   - already has an ink (set before a unit was chosen): the ink is shown,
 *     tinted with its series, and stays draggable to a unit.
 *   - no ink yet: "New ink" creates the master from the separation and assigns
 *     it in one step. The unit cells hold the ink picker itself.
 */
function PoolSep({ sku, sep, onNewInk }: {
  sku: string; sep: InkSeparation; onNewInk?: OnNewInk;
}) {
  const productsQ = useInkRegister();
  const products = productsQ.data ?? [];
  const current = sep.ink_code ? products.find((p) => p.ink_key === sep.ink_code) : undefined;
  const tint = current?.series_hex ?? null;

  return (
    <Box sx={{
      // display:flex, not the default block. ColourChip is inline-flex, and in a
      // block wrapper it lands in a TEXT LINE BOX that inherits the theme's 1.5
      // line-height — ~6px of dead space above and below every chip, which reads
      // as an extra carriage return once a cell holds five of them (Jack, 11/09).
      // As a flex item it has no line box at all.
      display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
      borderRadius: '5px', px: 0.4, py: 0.25,
      bgcolor: tint ? `${tint}33` : 'transparent',
      border: tint ? `1px solid ${tint}66` : '1px solid transparent',
    }}>
      <ColourChip sku={sku} sep={sep} />
      {sep.ink_code && (
        <Typography title={sep.ink_code} sx={{
          fontSize: 9.5, fontWeight: 600, lineHeight: 1.15, mt: 0.25,
          color: current?.low_migration ? LMQ_RED : TOKENS.ink2, overflowWrap: 'anywhere',
        }}>
          {current?.description ?? sep.ink_code}
          <Box component="span" sx={{ color: TOKENS.ink3, fontWeight: 500 }}> · drag to a unit</Box>
        </Typography>
      )}
    </Box>
  );
}

/**
 * Create an ink master from the separation that needs it, and assign it in the
 * same click. Pre-filled from the separation (code, name, kind, swatch); the
 * series is the one real decision. ink_key and description are composed by the
 * server from code × series, never typed — same rule as the catalogue screen.
 */
function NewInkDialog({ client, sku, sep, onClose, onCreated }: {
  client: string; sku: string; sep: InkSeparation; onClose: () => void;
  onCreated: (sku: string, sep: InkSeparation, inkKey: string) => void;
}) {
  const queryClient = useQueryClient();
  const draft = deriveColourCode(sep.name);
  const [colourCode, setColourCode] = useState(draft.code);
  const [colourName, setColourName] = useState(sep.name.toUpperCase());
  const [kind, setKind] = useState<string>(draft.kind);
  const [series, setSeries] = useState('');
  const [hex, setHex] = useState(sep.hex_colour ?? '');
  const [error, setError] = useState('');

  const seriesQ = useQuery({
    queryKey: productionKeys.inkSeries(false),
    queryFn: () => listInkSeries(false),
    staleTime: 60 * 1000,
  });
  const inkSeries = (seriesQ.data ?? []).filter((s) => s.material_class !== 'coating' && s.active);

  const create = useMutation({
    mutationFn: () => upsertInkProduct({
      colour_code: colourCode.trim().toUpperCase(),
      colour_name: colourName.trim().toUpperCase(),
      colour_kind: kind,
      series_suffix: series,
      hex_colour: hex.trim() || null,
    }),
    onSuccess: (product) => {
      queryClient.invalidateQueries({ queryKey: ['production', 'ink-products'] });
      onCreated(sku, sep, product.ink_key);
      onClose();
    },
    onError: (e) => {
      const x = e as { response?: { data?: { error?: string } }; message?: string };
      setError(x?.response?.data?.error ?? x?.message ?? 'Failed');
    },
  });

  const chosen = inkSeries.find((s) => s.series_suffix === series);
  const previewKey = colourCode.trim() && series ? `${colourCode.trim().toUpperCase()}${series}` : '';
  const valid = !!colourCode.trim() && !!colourName.trim() && !!series;

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>New ink for {sep.name}</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 12.5, color: TOKENS.ink3, mb: 1.5 }}>
          Nothing in the register covers this separation on {client} · {sku}. This creates the ink
          master and assigns it to the separation in one step.
        </Typography>
        <Stack spacing={1.5}>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction="row" spacing={1}>
            <TextField label="Colour code" size="small" value={colourCode} sx={{ width: 150 }}
              onChange={(e) => setColourCode(e.target.value.toUpperCase())}
              helperText="P185, C, or a short name" inputProps={{ style: { fontFamily: TOKENS.monoFont } }} />
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Kind</InputLabel>
              <Select label="Kind" value={kind} onChange={(e) => setKind(String(e.target.value))}>
                <MenuItem value="process">Process</MenuItem>
                <MenuItem value="pantone">Pantone</MenuItem>
                <MenuItem value="special">Special</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <TextField label="Colour name" size="small" fullWidth value={colourName}
            onChange={(e) => setColourName(e.target.value.toUpperCase())} />
          <FormControl size="small" fullWidth>
            <InputLabel>Series</InputLabel>
            <Select label="Series" value={series} onChange={(e) => setSeries(String(e.target.value))}>
              {inkSeries.map((s) => (
                <MenuItem key={s.series_suffix} value={s.series_suffix} sx={{ gap: 0.75 }}>
                  {s.display_hex && <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.display_hex }} />}
                  <span style={{ fontFamily: TOKENS.monoFont, fontWeight: 700, color: s.low_migration ? LMQ_RED : undefined }}>{s.series_suffix}</span>
                  <span style={{ color: '#888', fontSize: 12 }}>{s.manufacturer} {s.series_name}{s.low_migration ? ' · LMQ' : ''}</span>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField label="Swatch (hex)" size="small" value={hex} sx={{ width: 150 }}
              onChange={(e) => setHex(e.target.value)} helperText="from the artwork preview"
              inputProps={{ style: { fontFamily: TOKENS.monoFont } }} />
            <Box sx={{ width: 28, height: 28, borderRadius: '6px', border: '1px solid rgba(0,0,0,0.2)', bgcolor: hex || '#fff' }} />
          </Stack>
          {previewKey && (
            <Typography sx={{ fontSize: 12, color: TOKENS.ink2 }}>
              Will be created as <Box component="span" sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700 }}>{previewKey}</Box>
              {chosen ? ` — ${chosen.manufacturer} ${colourName.trim().toUpperCase()} ${chosen.series_name}` : ''}
              {chosen?.low_migration ? <Box component="span" sx={{ color: LMQ_RED, fontWeight: 700 }}> · low migration</Box> : null}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!valid || create.isPending}
          startIcon={create.isPending ? <CircularProgress size={14} color="inherit" /> : <AddRounded />}
          onClick={() => { setError(''); create.mutate(); }}
          sx={{ bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong } }}>
          Create and assign
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ColourChip({ sku, sep }: { sku: string; sep: InkSeparation }) {
  return (
    <Box
      draggable
      onDragStart={(e) => e.dataTransfer.setData(MIME, JSON.stringify({ sku, sep }))}
      title={sep.name}
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'grab',
        px: 0.5, py: '1px', borderRadius: '4px', bgcolor: '#fff',
        border: '1px solid rgba(0,0,0,0.15)', maxWidth: '100%',
        '&:active': { cursor: 'grabbing' },
      }}
    >
      <Box sx={{
        width: 11, height: 11, borderRadius: '2px', flexShrink: 0,
        bgcolor: sep.hex_colour ?? '#ccc', border: '1px solid rgba(0,0,0,0.15)',
      }} />
      <Typography sx={{ fontSize: 10, fontWeight: 600, lineHeight: 1.1, wordBreak: 'break-word' }}>
        {sep.name}
      </Typography>
    </Box>
  );
}

function SkuThumb({ client, sku }: { client: string; sku: string }) {
  const q = useQuery({
    queryKey: ['production', 'artwork-thumb-ref', client, sku, THUMB_W],
    queryFn: () => thumbLane(() => getArtworkThumbnailByRef(client, sku, THUMB_W)),
    enabled: !!client && !!sku,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    retry: false,
  });
  // Sized to fit INSIDE the 52px Art column (see FormeTable) — at 56px it
  // overflowed into Item and sat under the SKU code.
  const box = {
    width: 44, height: 34, flexShrink: 0, borderRadius: '5px',
    bgcolor: TOKENS.lineSoft, display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)',
  } as const;
  const url = proofScopeUrl(client, sku);

  let inner: React.ReactNode;
  if (q.isLoading) inner = <CircularProgress size={14} />;
  else if (q.data?.data_url) inner = <img src={q.data.data_url} alt="art" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />;
  else inner = <ImageNotSupportedRounded sx={{ fontSize: 18, color: 'rgba(0,0,0,0.25)' }} />;

  const clickable = url
    ? { cursor: 'pointer', transition: 'border-color 120ms', '&:hover': { borderColor: '#cb6015' } }
    : {};
  const content = (
    <Box
      sx={{ ...box, ...clickable }}
      onClick={url ? () => window.open(url, '_blank', 'noopener,noreferrer') : undefined}
    >
      {inner}
    </Box>
  );
  if (!url) return content;
  return <Tooltip title="Open in Cloudflow (ProofScope)" arrow>{content}</Tooltip>;
}
